import { FieldText, FormField } from '@dixa/lib-shared-habla';
import { ReactElement, useCallback } from 'react';
import { Field } from 'redux-form';

import { FieldTemplate } from 'components/fieldTemplate';
import { CARD_V2 } from 'components/shared/util/customCard';
import { useAppSelector } from 'utils/redux/useAppSelector';

import type { CardState, FormMode } from '../cards.types';
import { editorMaxLines, templateHelpText } from '../cards.utils';
import { CardsFormWrapper } from '../components/formWrapper';

import magentoTemplate from './fixtures/magentoTemplate.liquid.html';
import { apiKeyHelpText, subdomainHelpText } from './magentoCards.utils';

export interface MagentoCardsProps {
  mode: FormMode;
}

export function MagentoCards({ mode }: MagentoCardsProps): ReactElement {
  const { organizationId } = useAppSelector(({ settings }) => {
    return { organizationId: settings.organization.organization.id };
  });

  const magentoCardType = 'magento';

  const templateURL = useCallback(
    (formValues) =>
      `https://proxy.dixa/v1/proxy/${organizationId}/${magentoCardType}?apikey=${encodeURIComponent(
        formValues.hook.apikey,
      )}&api_hostname=${encodeURIComponent(
        formValues.hook.magentoSubdomain,
      )}&a_email={{a_email}}&a_phone_number={{a_phone_number}}`,
    [organizationId],
  );

  const getEditObject = useCallback(
    (formValues: CardState) =>
      ({
        card_type: magentoCardType,
        hook: {
          hook_id: formValues.hook.hook_id,
          title: formValues.hook.title,
          uri_template: templateURL(formValues),
          headers: {},
          card_version: formValues.hook.card_version,
        },
        template: formValues.template,
      } as CardState),
    [templateURL],
  );

  const getCreateObject = useCallback(
    (formValues: CardState) =>
      ({
        card_type: magentoCardType,
        hook: {
          title: formValues.hook.title,
          uri_template: templateURL(formValues),
          headers: {},
          // All new Cards are forced to use V2
          card_version: CARD_V2,
        },
        template: formValues.template,
      } as CardState),
    [templateURL],
  );

  return (
    <CardsFormWrapper
      form="magentoCardsForm"
      mode={mode}
      cardType={magentoCardType}
      getEditObject={getEditObject}
      getCreateObject={getCreateObject}
      template={magentoTemplate}
    >
      <>
        <FormField span={3}>
          <Field
            required
            label="Title"
            name="hook.title"
            component={FieldText}
            placeholder="Insert title"
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField span={3}>
          <Field
            required
            label="Subdomain"
            name="hook.magentoSubdomain"
            component={FieldText}
            helpIcon={{
              title: 'Subdomain help',
              text: subdomainHelpText,
            }}
            placeholder="Insert subdomain"
          />
        </FormField>
        <FormField span={3}></FormField>
        <FormField>
          <Field
            required
            label="API key"
            name="hook.apikey"
            component={FieldText}
            helpIcon={{
              title: 'API key help',
              text: apiKeyHelpText,
            }}
            placeholder="Insert API key"
          />
        </FormField>
        <FormField>
          <Field
            required
            defaultHidden
            label="Template content"
            name="template"
            component={FieldTemplate}
            helpIcon={{
              title: 'Template help',
              text: templateHelpText,
              requireClick: true,
            }}
            editorOptions={{ maxLines: editorMaxLines }}
            placeholder="Insert template"
          />
        </FormField>
      </>
    </CardsFormWrapper>
  );
}
